import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Briefcase, Building, Users, CheckCircle, TrendingUp, Calendar } from "lucide-react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CARD_ACCENTS = ["#06B6D4", "#0A7AFF", "#22C55E", "#F59E0B"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0C0C10] border border-[#252530] px-3 py-2">
      <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-widest text-[#EAFF00] mb-1">{label}</p>
      <p className="font-['JetBrains_Mono'] text-sm text-[#EEEADF]">{payload[0].value}</p>
    </div>
  );
};

const topCompanies = [
  { name: "Infosys", offers: 42, package: "₹6.5 LPA", fill: 0.93 },
  { name: "TCS", offers: 38, package: "₹7.2 LPA", fill: 0.84 },
  { name: "Google", offers: 5, package: "₹32 LPA", fill: 0.11 },
  { name: "Microsoft", offers: 8, package: "₹28 LPA", fill: 0.18 },
];

const upcomingDrives = [
  { company: "Amazon", date: "Mar 14", count: 28, type: "Online + HR" },
  { company: "Wipro", date: "Mar 18", count: 45, type: "Technical" },
  { company: "Cognizant", date: "Mar 22", count: 61, type: "Group Discussion" },
];

export default function PlacementDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { withCredentials: true })
      .then(r => setStats(r.data)).catch(() => {});
  }, []);

  const placementData = stats?.charts?.placements || [
    { branch: "CSE", placed: 87 }, { branch: "ECE", placed: 54 },
    { branch: "ME", placed: 38 }, { branch: "Civil", placed: 22 }, { branch: "EE", placed: 33 }
  ];

  const statCards = [
    { label: "Active Jobs", value: stats?.stats?.active_jobs || 34, sub: "Open positions" },
    { label: "Companies", value: stats?.stats?.total_companies || 67, sub: "Registered this year" },
    { label: "Placed Students", value: stats?.stats?.placed_students || 234, sub: `${stats?.stats?.placement_rate || 78.5}% rate` },
    { label: "Avg. Package", value: `₹${stats?.stats?.avg_package_lpa || 8.5}L`, sub: "CTC offered" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} data-testid="placement-dashboard" className="space-y-6">
      <div className="border-b border-[#D8D4CB] pb-5">
        <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Placement Cell</p>
        <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Placement Dashboard</h2>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((s, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`placement-stat-${i}`}
            className="bg-white border border-[#E2DDD4] p-5 hover:border-[#0C0C10] transition-colors"
            style={{ borderLeft: `3px solid ${CARD_ACCENTS[i]}` }}
          >
            <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-3">{s.label}</p>
            <p className="font-['JetBrains_Mono'] font-bold text-[2.2rem] text-[#0C0C10] leading-none">{s.value}</p>
            <p className="font-['DM_Sans'] text-xs text-[#7A7468] mt-3">{s.sub}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="placements-chart">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-1">Placements by Branch</p>
          <p className="font-['Syne'] font-bold text-sm text-[#0C0C10] mb-5">Students placed per department</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={placementData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F0ECE4" />
              <XAxis dataKey="branch" tick={{ fontSize: 9, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#BDBAA8", fontFamily: "Syne" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="placed" fill="#06B6D4" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top companies */}
        <div className="bg-white border border-[#E2DDD4] p-6" data-testid="top-companies">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8] mb-4">Top Recruiters</p>
          <div className="space-y-4">
            {topCompanies.map((c, i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="w-8 h-8 bg-[#06B6D4]/10 border border-[#06B6D4]/20 flex items-center justify-center flex-shrink-0">
                  <span className="font-['Syne'] font-bold text-sm text-[#06B6D4]">{c.name.charAt(0)}</span>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1.5">
                    <span className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{c.name}</span>
                    <span className="font-['JetBrains_Mono'] text-xs text-[#7A7468]">{c.offers} offers — {c.package}</span>
                  </div>
                  <div className="w-full h-1 bg-[#F0ECE4]">
                    <div className="h-1 bg-[#06B6D4] transition-all duration-500" style={{ width: `${c.fill * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming drives */}
      <div className="bg-white border border-[#E2DDD4] p-6" data-testid="upcoming-interviews">
        <div className="flex items-center justify-between mb-4">
          <p className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">Upcoming Interview Drives</p>
          <button data-testid="schedule-drive-btn" className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#06B6D4] border border-[#06B6D4]/30 px-3 py-1.5 hover:bg-[#06B6D4]/8 transition-colors">
            + Schedule Drive
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {upcomingDrives.map((iv, i) => (
            <div key={i} className="p-4 border border-[#06B6D4]/20 bg-[#06B6D4]/5">
              <div className="flex items-center justify-between mb-3">
                <span className="font-['Syne'] font-bold text-sm text-[#0C0C10]">{iv.company}</span>
                <span className="font-['JetBrains_Mono'] text-xs text-[#06B6D4]">{iv.date}</span>
              </div>
              <p className="font-['DM_Sans'] text-xs text-[#7A7468] mb-3">{iv.type}</p>
              <div className="flex items-center gap-1.5">
                <Users className="w-3 h-3 text-[#BDBAA8]" />
                <span className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#BDBAA8]">{iv.count} students</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
